import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { paymentsApi } from '@/lib/api';
import { 
  Truck, 
  CheckCircle,
  Loader2,
  ArrowRight
} from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      toast.error('Payment verification timed out. Please check your dashboard.');
      return;
    }

    try {
      const response = await paymentsApi.getStatus(sessionId);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div 
        className="max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
            THE HOTSHOT MARKETPLACE
          </span>
        </Link>

        {status === 'checking' && (
          <div className="bg-card border border-border rounded-sm p-8">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              VERIFYING PAYMENT
            </h1>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-card border border-border rounded-sm p-8">
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              PAYMENT SUCCESSFUL!
            </h1>
            <p className="text-muted-foreground mb-6">
              Your payment has been processed. The job is now in progress.
            </p>
            {paymentData && (
              <div className="bg-muted/50 rounded-sm p-4 mb-6 text-left">
                <p className="text-sm text-muted-foreground">Amount Paid</p>
                <p className="text-2xl font-bold price-display">
                  A${paymentData.amount?.toFixed(2)}
                </p>
              </div>
            )}
            <Button 
              className="w-full btn-active gap-2"
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {(status === 'failed' || status === 'error' || status === 'timeout') && (
          <div className="bg-card border border-border rounded-sm p-8">
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">!</span>
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              {status === 'timeout' ? 'VERIFICATION TIMEOUT' : 'PAYMENT ISSUE'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {status === 'timeout' 
                ? 'Payment verification timed out. Please check your dashboard for status.'
                : 'There was an issue with your payment. Please try again.'}
            </p>
            <Button 
              className="w-full btn-active gap-2"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
